import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faCircleExclamation, faDesktop, faDoorOpen, faEllipsisV, faPhone, faRotateRight, faTrash } from "@fortawesome/pro-regular-svg-icons";
import BookingSourceIcon from "components/bookingSourceLogo/BookingSourceIcon";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import useModal from "hooks/modals/useModal";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import moment from "moment";

import { closeEditBooking, dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import { IBooking, ISlot, ITeeTime } from "redux/reducers/models/teetime";

import { StatusCode } from "api/protocols";

import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { customerErrorMessage, delay, convertHoles } from "helpers/Helpers";
import { isTurnBlockedForEighteenHoles } from "helpers/teeSheetTurn";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import { Select } from "components/select/index";
import { ButtonNew as Button, ButtonNew } from "components/buttonNew/index";
import AuditTrailNote from "components/bookingPopUp/auditTrailNote/AuditTrailNote";
import Radio from "components/radio";
import { Dropdown } from "components/dropdown/dropdown";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import CreditCardCatalogue from "elements/teesheet/creditCard/CreditCardCatalogue";
import CreditCardAdd from "elements/teesheet/creditCard/CreditCardAdd";
import { useTranslation } from "react-i18next";
import MoveBookingTab from "elements/teesheet/MoveBookingTab";
import DuplicateBookingTab from "elements/teesheet/DuplicateBookingTab";
import Portal from "elements/Portal";
import TextField from "components/form/textField/TextField";
import Spin from "components/spin/spin";

import "public/scss/teesheet.scss";
import "elements/teesheet/bookingModal.scss";
import {
  GetCustomer,
  PostCustomer,
  GetCustomerPaymentMethod,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetCustomerFriends, ICustomerFriend } from "api/rpc/2024-04/facilityAdmin/customer/friend";
import {
  DeleteBookingNote,
  GetAuditTrails,
  GetBookingNotes,
  PostBookingNote,
  PutBooking,
  PutBookingConfirmation,
  PutCancelBooking,
  PutCopyBooking,
  PutMoveBooking,
} from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { DeleteTeeTimeLocks } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import Checkbox from "components/form/checkbox/Checkbox";
import { TLoadingUpdateBookingAction } from "../loadingTeetimeTabActions";
import { Badge } from "components/badge/Badge";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Popup from "components/popup/Popup";
import Toggle from "components/form/toggle/Toggle";
import PlayerSlots from "components/bookingPopUp/playersSlots/PlayerSlots";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import CardSection from "components/cardSection/CardSection";
import Card from "components/card/Card";
import SelectNew from "components/select/SelectNew";

function isLockedBookingSource(source: string | undefined) {
  return source === "online" || source === "mobile";
}

export interface ICartsAvailableEdit {
  nineHole: number;
  eighteenHole: number;
}

interface IBookingModalEditProps {
  cartsAvailable: ICartsAvailableEdit;
  selectedBooking: IBooking;
  teeTimeLock: boolean;
  /**Optional boolean - Refresh selected tee time when modal is closed. Set to true on default. */
  refreshTeeTime?: boolean;
  loading: boolean;
  loadingText: string;
  reloadTeeTimes: () => void;
  reloadTeeTime: (updateTeetimeTabAction: TLoadingUpdateBookingAction) => void;
  openDuplicateBookingModal: (booking: IBooking, teetime: ITeeTime) => void;
}

interface IMainState {
  [key: string]: any;
  golferSearchQuery1: string;
  golferSearchQuery2: string;
  golferSearchQuery3: string;
  golferSearchQuery4: string;
  golferSearchQuery5: string;
  golferSearchQuery6: string;
  golferSearchResult1: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult2: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult3: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult4: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult5: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult6: (Partial<ICustomer> | ICustomer)[];
  playingPartners: Partial<ICustomer>[];
  searching: boolean;
}

export interface INewPlayerState {
  newCustomer: ICustomer;
  selectedSlot: ISlot;
  newCustomerSheetActive: boolean;
  currentSearch: string;
}

interface IReplacePlayerState {
  showReplacePlayer: boolean;
  showNewPlayer: boolean;
  customerQuery: string;
  customerSearching: boolean;
  playerSearchResult: ICustomer[];
  selectedCustomer: ICustomer | null;
  currentCustomer: ICustomer | null;
  slot: ISlot | null;
  slotFound: { id: number; customer_id: number; guest: boolean } | null;
}

export interface IEditBookingState {
  holes: number;
  quantity: number;
  powerCartQuantity: number;
  teeTimeSlots: { id: number; customer_id: number; guest: boolean }[];
  loadedTeeTimeSlots: { id: number; customer_id: number; guest: boolean }[];
  customerSelection: ICustomer[];
  originalCustomerSelection: ICustomer[];
  customer: any;
  customers: [];
  golferInitialLoad: any;
  cartInitialLoad: any;
  holesInitialLoad: any;
  saveCard: boolean;
  addCard: boolean;
  auditTrails: any;
  notes: any;
  addNoteVisible: string;
  notesTitleVisible: string;
  paymentMethods: any[];
  selectedPaymentMethod?: number;
  cardReady: boolean;
  copyBooking: boolean;
  copyBookingIdList: any;
  copyBookingFutureDates: any;
  copyBookingDate: string;
  copyBookingTime: string;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };

  holeButtons: boolean[]; // disabled
  playerButtons: boolean[]; // disabled
  cartButtons: boolean[]; // disabled

  bookingWithinHour: boolean;

  bookingPosting: boolean;

  send_cancellation_email: boolean;
  sourceDropdownVisible: boolean;
  source: string;
}

enum StateType {
  MainState = "IMainState",
  EditBookingState = "IEditBookingState",
  NewPlayerState = "INewPlayerState",
}

const BookingModalEdit: React.FC<IBookingModalEditProps> = props => {
  const { Option } = Select;
  const windowSize = useWindowSize();
  const history = useHistory();
  const { authStore, teeSheetStore, uiStore, facilityStore } = useAppSelector(state => state);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = authStore.user?.permissions;


  const { selectedBooking, teeTimeLock, openDuplicateBookingModal } = props;

  const [newPlayerState, setNewPlayerState] = useState<INewPlayerState>({
    newCustomer: null,
    selectedSlot: null,
    newCustomerSheetActive: false,
    currentSearch: "",
  });

  const [moveBookingPopoverIsVisible, setMoveBookingPopoverIsVisible] = useState(false);
  const [duplicateBookingPopoverIsVisible, setDuplicateBookingPopoverIsVisible] = useState(false);
  const [cancelBookingPopoverIsVisible, setCancelBookingPopoverIsVisible] = useState(false);

  const [replacePlayerState, setReplacePlayerState] = useState<IReplacePlayerState>({
    showReplacePlayer: false,
    showNewPlayer: false,
    customerQuery: "",
    customerSearching: false,
    playerSearchResult: [],
    selectedCustomer: null,
    currentCustomer: null,
    slot: null,
    slotFound: null,
  });

  const [state, setState] = useState<IMainState>({
    golferSearchQuery1: "",
    golferSearchQuery2: "",
    golferSearchQuery3: "",
    golferSearchQuery4: "",
    golferSearchQuery5: "",
    golferSearchQuery6: "",
    golferSearchResult1: [null],
    golferSearchResult2: [null],
    golferSearchResult3: [null],
    golferSearchResult4: [null],
    golferSearchResult5: [null],
    golferSearchResult6: [null],
    playingPartners: [],
    searching: false,
  });

  const [editBookingState, setEditBookingState] = useState<IEditBookingState>({
    holes: /*18*/ null,
    quantity: /*2*/ null,
    powerCartQuantity: /*0*/ null,
    teeTimeSlots: [],
    loadedTeeTimeSlots: [],
    customerSelection: [],
    originalCustomerSelection: [],
    customer: null,
    customers: [],
    golferInitialLoad: null,
    cartInitialLoad: null,
    holesInitialLoad: null,
    notes: [],
    auditTrails: [],
    addNoteVisible: "none",
    notesTitleVisible: "none",
    saveCard: false,
    addCard: false,
    paymentMethods: undefined,
    selectedPaymentMethod: undefined,
    cardReady: false,
    copyBooking: false,
    copyBookingIdList: [],
    copyBookingFutureDates: [],
    copyBookingDate: "",
    copyBookingTime: "",
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },

    holeButtons: [false, false], // disabled
    playerButtons: [false, false, false, false], // disabled
    cartButtons: [false, false], // disabled

    bookingWithinHour: false,

    bookingPosting: false,

    send_cancellation_email: true,
    sourceDropdownVisible: false,
    source: "tee_sheet",
  });

  const sourceRef = useRef(null);
  useOutsideAlerter(sourceRef, () =>
    updateState<IEditBookingState>({ sourceDropdownVisible: false }, StateType.EditBookingState),
  );

  function getBookingNineHoleCartsAvailable() {
    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      const cartsAvailable9 =
        props.cartsAvailable?.nineHole == null || props.cartsAvailable.nineHole < 0 ? 0 : props.cartsAvailable.nineHole;

      return cartsAvailable9 + (selectedBooking?.power_cart_quantity ?? 0);
    } else {
      return 0;
    }
  }

  const bookingNineHoleCartsAvailable = getBookingNineHoleCartsAvailable();

  function getBookingEighteenHoleCartsAvailable() {
    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      const cartsAvailable18 =
        props.cartsAvailable?.eighteenHole == null || props.cartsAvailable.eighteenHole < 0
          ? 0
          : props.cartsAvailable.eighteenHole;

      return cartsAvailable18 + (selectedBooking?.power_cart_quantity ?? 0);
    } else {
      return 0;
    }
  }

  const bookingEighteenHoleCartsAvailable = getBookingEighteenHoleCartsAvailable();

  function getBookingCartsAvailable() {
    if (!facilityStore.teeSheetSettings?.validate_power_cart_count) {
      return 0;
    } else if (editBookingState.holes === 9) {
      return bookingNineHoleCartsAvailable;
    } else if (editBookingState.holes === 18) {
      return bookingEighteenHoleCartsAvailable;
    } else {
      return 0;
    }
  }

  const bookingCartsAvailable = getBookingCartsAvailable();

  function updateState<T>(newState: Partial<T>, type: StateType) {
    const states = {
      [StateType.MainState]: (newState: Partial<T>) =>
        setState((cur: IMainState) => {
          return { ...cur, ...newState };
        }),
      [StateType.EditBookingState]: (newState: Partial<T>) =>
        setEditBookingState((cur: IEditBookingState) => {
          return { ...cur, ...newState };
        }),
      [StateType.NewPlayerState]: (newState: Partial<T>) =>
        setNewPlayerState((cur: INewPlayerState) => {
          return { ...cur, ...newState };
        }),
    };

    states[type](newState);
  }

  useEffect(() => {
    if (uiStore.editBookingActive && teeSheetStore.selectedTeeTime && selectedBooking) {
      const teeTimeSlots: any[] = [];
      const customerSelection: any[] = [];
      teeSheetStore.selectedTeeTime?.slots
        .filter(slot => slot.tee_time_booking_id === selectedBooking?.id)
        .map(slot => {
          if (slot.status === "booked") {
            customerSelection.push(slot.booking_participant.customer);
            teeTimeSlots.push({
              id: slot.id,
              customer_id: slot.booking_participant.customer_id,
              guest: slot.booking_participant.guest,
            });
          }
        });

      updateState<IEditBookingState>(
        {
          holes: selectedBooking.holes,
          quantity: selectedBooking.quantity,
          powerCartQuantity: selectedBooking.power_cart_quantity,
          selectedPaymentMethod: selectedBooking.customer_payment_method_id,
          source: selectedBooking.source || "tee_sheet",
          customerSelection,
          originalCustomerSelection: [...customerSelection],
          teeTimeSlots,
          loadedTeeTimeSlots: teeTimeSlots.slice(),
        },
        StateType.EditBookingState,
      );

      // load paymentMethods on first load
      void loadCustomersPaymentMethods(Number(customerSelection[0].id));
      void loadAuditTrail();
    }
  }, [selectedBooking]);

  const getBookingNotes = async () => {
    const getBookingNotesRes = await GetBookingNotes({ tee_time_booking_id: selectedBooking.id }, true);
    if (getBookingNotesRes.status !== StatusCode.OK) {
      dispatch(showError(getBookingNotesRes.message));
      return;
    }
    let tempNotes = getBookingNotesRes.data;
    if (tempNotes?.length > 0) {
      tempNotes = tempNotes.reverse();
    }
    updateState<IEditBookingState>(
      {
        notes: tempNotes,
      },
      StateType.EditBookingState,
    );
  };

  const handleUpdateBooking = async () => {
    const teeTimeSlots = editBookingState.teeTimeSlots;

    // Handle booking guests
    if (editBookingState.quantity > editBookingState.teeTimeSlots.length) {
      const diff = editBookingState.quantity - editBookingState.teeTimeSlots.length;
      //Get all slots that have been freed up
      const availableSlots = editBookingState.loadedTeeTimeSlots.filter(
        initialSlot => !editBookingState.teeTimeSlots.some(slot => slot.id === initialSlot.id),
      );
      teeSheetStore.selectedTeeTime.slots.forEach(slot => {
        //Get all available slots
        if (slot.status === "available") {
          availableSlots.push({ id: slot.id, customer_id: editBookingState.customerSelection[0].id, guest: true });
        }
      });
      //Set require amount of slots as guests
      for (let i = 0; i < diff; i++) {
        teeTimeSlots.push({
          id: availableSlots[i].id,
          customer_id: editBookingState.customerSelection[0].id,
          guest: true,
        });
      }

      updateState<IEditBookingState>({ teeTimeSlots }, StateType.EditBookingState);
    }

    //If no payment method is selected, add new card & set selected payment method as the new card
    if (elementsComplete) {
      const collectCardRes = await collectCard(selectedBooking.id, selectedBooking.customer_id);
      if (!collectCardRes) {
        return;
      }
    } else {
      updateState<IEditBookingState>({ bookingPosting: true }, StateType.EditBookingState);
      const res = await PutBooking(
        {
          id: selectedBooking.id,
          quantity: Number(editBookingState.quantity),
          holes: Number(editBookingState.holes),
          power_cart_quantity: Number(editBookingState.powerCartQuantity),
          customer_payment_method_id: editBookingState.selectedPaymentMethod,
          tee_time_slots: editBookingState.teeTimeSlots,
          ...(facilityStore.teeSheetSettings?.display_booking_source
            ? {
                source: isLockedBookingSource(selectedBooking?.source)
                  ? selectedBooking.source
                  : editBookingState.source,
              }
            : {}),
        },
        false,
      );

      if (res.status !== StatusCode.OK) {
        updateState<IEditBookingState>({ bookingPosting: false }, StateType.EditBookingState);

        if (res.status === StatusCode.BAD_REQUEST) {
          dispatch(showError(res.data as unknown as string));
        } else {
          dispatch(showError(t("Error updating booking")));
        }
      } else {
        //Post booking note if textfield contains text
        // if (editBookingState.noteDescription !== "") {
        //   // const noteInfo = {
        //   //   tee_time_booking_id: selectedBooking.id,
        //   //   content: String(editBookingState.noteDescription),
        //   // };

        //   // const bookingNoteRes = await PostBookingNote(noteInfo, true);

        //   void handleAddNote();
        // }

        updateState<IEditBookingState>({ bookingPosting: false }, StateType.EditBookingState);
        dispatch(showSuccess(t("Booking updated successfully")));
      }
    }

    closeBooking();

    void props.reloadTeeTime({
      type: "update-booking",
      id: teeSheetStore.selectedTeeTime.id,
    });
  };


  const {
    state: addCardModal,
    updateModal: updateAddCardModal,
    closeModal: closeAddCardModal,
  } = useModal({ isOpen: false });


  //FUNCTIONS

  function closeBooking() {
    updateState<IEditBookingState>(
      {
        customerSelection: [],
        originalCustomerSelection: [],
        teeTimeSlots: [],
        addNoteVisible: "none",
        notesTitleVisible: "none",
        // noteDescription: "",
        notes: [],
        holes: null,
        quantity: null,
        powerCartQuantity: null,
        copyBooking: false,
        copyBookingIdList: [],
        copyBookingFutureDates: [],
        copyBookingDate: "",
        copyBookingTime: "",
        selectedPaymentMethod: null,
        paymentMethods: undefined,
        addCard: false,
        saveCard: false,
        elementComplete: {
          cardNumber: false,
          cardExpiry: false,
          cardCvc: false,
        },
        auditTrails: [],
        bookingPosting: false,
        sourceDropdownVisible: false,
        source: "tee_sheet",
      },
      StateType.EditBookingState,
    );

    // clear search and result
    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
        golferSearchResult1: [null],
        golferSearchResult2: [null],
        golferSearchResult3: [null],
        golferSearchResult4: [null],
        golferSearchResult5: [null],
        golferSearchResult6: [null],
        playingPartners: [],
        searching: false,
      },
      StateType.MainState,
    );

    void deleteTeeTimeLocks();
    dispatch(closeEditBooking());

    setSelected(0);
    handleSecondaryTabToggle();
    // if (refreshTeeTime) {
    //   // force refresh, removes player
    //   void dispatch(selectTeeTime(teeSheetStore.selectedTeeTime.id, true));
    // }
  }

  async function deleteTeeTimeLocks() {
    const deleteLocksRes = await DeleteTeeTimeLocks(false);
    if (deleteLocksRes.status !== StatusCode.OK) {
      console.log("Delete tee time locks error");
      return;
    }
  }

  /**
   * compares tee_time_quantity and turn_tee_time quantity and returns the lower quantity
   * if turn_tee_time_quantity is null turn_tee_time will be returned
   * @param tee_time_quantity
   * @param turn_tee_time_quantity
   * @returns number
   */
  const lowerQuantity = (tee_time_quantity: number, turn_tee_time_quantity: number, selected_holes: number): number => {
    if (turn_tee_time_quantity === null) {
      return tee_time_quantity;
    } else if (selected_holes === 9) {
      return tee_time_quantity;
    } else {
      return Math.min(tee_time_quantity, turn_tee_time_quantity);
    }
  };

  useEffect(() => {
    // disabling options...
    let mounted = true;

    const disableOptions = () => {
      const holeButtons: boolean[] = [true, true];
      let playerButtons: boolean[] = teeSheetStore.selectedTeeTime?.slots?.map(() => false);

      let quantity_remaining = lowerQuantity(
        teeSheetStore.selectedTeeTime?.quantity_remaining,
        teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining,
        editBookingState.holes,
      );

      // Disable additional player buttons to add players if tee time is locked
      if (!teeTimeLock) {
        quantity_remaining += editBookingState.loadedTeeTimeSlots.length;
      } else {
        quantity_remaining = editBookingState.loadedTeeTimeSlots.length;
      }

      if (editBookingState.quantity > quantity_remaining) {
        updateState<IEditBookingState>({ quantity: null }, StateType.EditBookingState);
      }

      // disabling number of players based on quantity remaining
      switch (quantity_remaining) {
        case 1:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 2:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0 || i === 1) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 3:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i !== 3) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 4:
          playerButtons = playerButtons.map(() => {
            return false;
          });
          break;

        case 5:
          playerButtons = playerButtons.map(() => {
            return false;
          });
          break;

        case 6:
          playerButtons = playerButtons.map(() => {
            return false;
          });
          break;
      }

      if (selectedBooking?.holes !== 18) {
        // Disabling holes
        teeSheetStore.selectedTeeTime?.divisions_available?.forEach(division => {
          // 9 holes available
          if (division === 1) {
            holeButtons[1] = false;
          } else if (division === 2) {
            // Only 9 holes available
            if (
              (teeSheetStore.selectedTeeTime?.divisions_available?.length > 1 &&
                teeSheetStore.selectedTeeTime?.turn_tee_time === null) ||
              isTurnBlockedForEighteenHoles(
                teeSheetStore?.selectedTeeTime?.turn_tee_time?.blocked_type,
                teeSheetStore?.selectedTeeTime?.blocked_type,
              ) ||
              ((teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
                teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id) &&
                (teeSheetStore.selectedTeeTime?.league_id !== teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
                  teeSheetStore.selectedTeeTime?.tournament_id !==
                  teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id))
            ) {
              holeButtons[1] = false;
            } else if (teeSheetStore.selectedTeeTime?.turn_tee_time != null) {
              // 18 holes available
              if (teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining >= editBookingState.quantity) {
                holeButtons[0] = false;
              } else {
                // Only 9 holes available
                holeButtons[1] = false;
              }
            }
          }
        });
      } else {
        holeButtons.fill(false);
      }

      if (mounted) {
        if (holeButtons[0] && holeButtons[1]) {
          updateState<IEditBookingState>({ holeButtons, playerButtons, holes: null }, StateType.EditBookingState);
        } else if (holeButtons[0]) {
          updateState<IEditBookingState>({ holeButtons, playerButtons, holes: 9 }, StateType.EditBookingState);
        } else if (holeButtons[1]) {
          updateState<IEditBookingState>({ holeButtons, playerButtons, holes: 18 }, StateType.EditBookingState);
        }
        updateState<IEditBookingState>({ holeButtons, playerButtons }, StateType.EditBookingState);
      }
    };

    const run = async () => {
      if (uiStore.editBookingActive === true) {
        // dispatch(enqueue());
        await delay(100);

        disableOptions();

        await delay(1000);
        // dispatch(dequeue());
      }
    };
    void run();

    return () => {
      mounted = false;
    };
  }, [teeSheetStore.selectedTeeTime, editBookingState.holes, uiStore.editBookingActive, editBookingState.quantity]);

  const confirmCancelBooking = async () => {
    try {
      const putCancelRes = await PutCancelBooking(
        {
          tee_time_booking_id: Number(selectedBooking.id),
          send_cancellation_email: editBookingState.send_cancellation_email,
        },
        true,
      );

      if (putCancelRes.status === StatusCode.OK) {
        closeBooking();
        dispatch(showSuccess(t("Successfully cancelled booking")));
      }

      if (putCancelRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error cancelling booking")));
      }

      props.reloadTeeTimes();
    } catch (e) {
      console.log(e);
    }
  };

  const confirmMoveBooking = async (selectedTeeTimeId: number, resend: boolean) => {
    if (selectedTeeTimeId > 0) {
      const params = {
        booking_id: Number(selectedBooking.id),
        tee_time_id: selectedTeeTimeId,
      };

      const moveBookingRes = await PutMoveBooking(params, true);

      if (moveBookingRes.status !== StatusCode.OK) {
        if (moveBookingRes.status === StatusCode.BAD_REQUEST) {
          dispatch(showError(String(moveBookingRes.data)));
        } else {
          dispatch(showError(t("Could not move booking")));
        }

        return;
      }

      dispatch(showSuccess(t("Booking moved successfully")));

      if (resend) {
        await handleResendConfirmation();
      }

      dispatch(closeEditBooking());

      props.reloadTeeTimes();

      await PutBookingConfirmation({ booking_group_id: selectedBooking.booking_group_id }, true);

      history.push(`/admin/teesheet`);
    }
  };

  const addCopyBookingIdToList = (copyBookingId: number, copyBookingTime: string, copyBookingDate: string) => {
    if (copyBookingTime === null) {
      dispatch(showError(t("Please select a time.")));
      return;
    }
    const tempList = editBookingState.copyBookingIdList;
    const currentCopyDates = editBookingState.copyBookingFutureDates;

    if (tempList.includes(copyBookingId) === false) {
      const dateString = copyBookingDate + " at " + copyBookingTime;
      currentCopyDates.push(dateString);
      tempList.push(copyBookingId);

      updateState<IEditBookingState>(
        { copyBookingIdList: tempList, copyBookingFutureDates: currentCopyDates },
        StateType.EditBookingState,
      );
    } else {
      dispatch(showError(t("Teetime ID already in list.")));
    }
  };

  const confirmCopyBooking = async (copyBookingId: number | string) => {
    const tempIdList = editBookingState.copyBookingIdList;

    if (copyBookingId !== undefined && copyBookingId !== "") {
      if (tempIdList.includes(Number(copyBookingId)) === false) {
        tempIdList.push(copyBookingId);
      } else {
        dispatch(showError(t("Teetime ID already in list.")));
      }
    }

    //API CALL
    if (tempIdList.length !== 0) {
      if (tempIdList[0] === null) {
        updateState<IEditBookingState>({ copyBookingIdList: [] }, StateType.EditBookingState);
        return;
      }

      const params = {
        booking_id: Number(selectedBooking.id),
        tee_time_ids: tempIdList,
      };

      const copyBookingRes = await PutCopyBooking(params, true);

      if (copyBookingRes.status !== StatusCode.OK) {
        if (copyBookingRes.status === StatusCode.BAD_REQUEST) {
          dispatch(showError(String(copyBookingRes.data)));
        } else {
          dispatch(showError(t("Could not copy booking")));
        }

        updateState<IEditBookingState>({ copyBookingIdList: [] }, StateType.EditBookingState);
        return;
      }

      dispatch(showSuccess(t("Booking copied successfully")));
      dispatch(closeEditBooking());

      props.reloadTeeTimes();

      history.push(`/admin/teesheet`);
    }
  };

  async function loadCustomers(params: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: params }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const search = async (
    mounted: boolean,
    golferSearchQuery: string,
    golferSearchResult: string,
    cancelToken: CancelToken,
  ) => {
    try {
      if (golferSearchQuery === "") {
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: [null] }, StateType.MainState);
        }
        return;
      } else {
        updateState<IMainState>({ searching: true }, StateType.MainState);
        const res = await loadCustomers(golferSearchQuery, cancelToken);
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: res, searching: false }, StateType.MainState);
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery1, "golferSearchResult1", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult1: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery1]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery2, "golferSearchResult2", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult2: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery2]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery3, "golferSearchResult3", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult3: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery3]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery4, "golferSearchResult4", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult4: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery4]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery5, "golferSearchResult5", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult5: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery5]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery6, "golferSearchResult6", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult6: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery6]);

  const handleCustomerSearch = (query: string, name: string) => {
    updateState<IMainState>({ [name]: query }, StateType.MainState);
    setNewPlayerState(prevState => ({
      ...prevState,
      currentSearch: query,
    }));
  };

  const handleCustomerSelection = (id: number | string, slot: ISlot, newCustomer: Record<string, any>) => {
    if (editBookingState.customerSelection.length === 4) {
      console.log("max players added, remove 1 or more players to add more");
      return;
    }

    if (id === "") {
      return;
    } else {
      const golferPosition = slot.position.toString();
      const queryKey = "golferSearchQuery".concat(golferPosition);
      const searchResultKey = "golferSearchResult".concat(golferPosition);

      const selection = !newCustomer
        ? (((): (Partial<ICustomer> | ICustomer)[] => {
          const candidates = state[queryKey]
            ? (state[searchResultKey] as (Partial<ICustomer> | ICustomer)[])
            : state.playingPartners;

          const selectedCustomer = (candidates ?? []).find(
            candidate => candidate != null && Number(candidate.id) === Number(id),
          );

          return selectedCustomer ? [selectedCustomer] : [];
        })())
        : [newCustomer];

      if (!selection[0]) {
        return;
      }

      const customerSelection = editBookingState.customerSelection;
      customerSelection.splice(slot.position - 1, 0, selection[0] as ICustomer);

      const teeTimeSlots = editBookingState.teeTimeSlots;
      teeTimeSlots.splice(slot.position - 1, 0, { id: slot.id, customer_id: selection[0].id, guest: false });

      updateState({ customerSelection, teeTimeSlots }, StateType.EditBookingState);
    }
  };

  const tabs = [
    {
      id: "booking-details",
      content: t("General Info"),
    },
    {
      id: "credit-card",
      content: t("Payment Method"),
    },
    {
      id: "notes",
      content: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {t("Notes")}
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '20px',
            height: '20px',
            padding: '0 6px',
            backgroundColor: '#F3F4F6',
            color: '#6B7280',
            borderRadius: '10px',
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '1',
          }}>
            {editBookingState.notes?.length || 0}
          </span>
        </span>
      ),
    },
    {
      id: "audit-trail",
      content:
        windowSize.width <= MOBILE_WIDTH ? (
          <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
        ) : (
          t("Activity History")
        ),
    },
  ];

  const closeNewCustomer = () => {
    updateState<INewPlayerState>(
      {
        newCustomer: null,
        selectedSlot: null,
        newCustomerSheetActive: false,
      },
      StateType.NewPlayerState,
    );

    updateState<IMainState>(
      { golferSearchQuery1: "", golferSearchQuery2: "", golferSearchQuery3: "", golferSearchQuery4: "", golferSearchQuery5: "", golferSearchQuery6: "" },
      StateType.MainState,
    );
  };

  const addCustomer = async (customerInfo: ICustomerInfoState) => {
    const customer = await createCustomer(customerInfo);
    if (customer) {
      handleCustomerSelection(customer.id, newPlayerState.selectedSlot, customer);
    }
  };

  const createCustomer = async (customerInfo: ICustomerInfoState) => {
    const error =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (error) {
      console.log("missing parameters");
      return null;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        state: "disabled",
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (Number(customerRes.status) !== 200) {
      dispatch(showError(customerErrorMessage(t, customerRes?.data?.message as string)));
    }

    if (Number(customerRes.status) === 200) {
      updateState<INewPlayerState>({ newCustomer: customerRes.data.data }, StateType.NewPlayerState);
      void closeNewCustomer();
      // dispatch(showSuccess(customerRes.data.message));
      dispatch(showSuccess(t("Customer created successfully")));
      return customerRes.data as ICustomer;
    }
    return null;
  };

  const removeGolfer = (slot: { id: number; customer_id: number; guest: boolean }) => {
    const deleteIndex = editBookingState.teeTimeSlots.indexOf(slot);
    let customerSelection = editBookingState.customerSelection;
    let teeTimeSlots = editBookingState.teeTimeSlots;
    let selectedPaymentMethod = editBookingState.selectedPaymentMethod;

    if (deleteIndex === 0) {
      selectedPaymentMethod = undefined;
    }

    //Get the number of guests the selected player has (+ themselves)
    const playerAndGuests = editBookingState.teeTimeSlots.filter(
      teeTimeSlot => teeTimeSlot.customer_id === slot.customer_id,
    );

    //If the selected player is not a guest but has guests, remove the player and guests
    if (playerAndGuests.length > 1 && slot.guest === false) {
      customerSelection = customerSelection.filter(customer => customer.id !== slot.customer_id);
      teeTimeSlots = teeTimeSlots.filter(teeTimeSlots => teeTimeSlots.customer_id !== slot.customer_id);
      //Remove single player
    } else if (deleteIndex !== -1) {
      customerSelection.splice(deleteIndex, 1);
      teeTimeSlots.splice(deleteIndex, 1);
    } else {
      return;
    }

    updateState<IEditBookingState>(
      { customerSelection, teeTimeSlots, selectedPaymentMethod },
      StateType.EditBookingState,
    );
  };

  const openReplacePlayer = (
    slot: ISlot,
    slotFound: { id: number; customer_id: number; guest: boolean },
    golfer: ICustomer,
  ) => {
    setReplacePlayerState({
      showReplacePlayer: true,
      showNewPlayer: false,
      customerQuery: "",
      customerSearching: false,
      playerSearchResult: [],
      selectedCustomer: null,
      currentCustomer: golfer,
      slot,
      slotFound,
    });
  };

  const closeReplacePlayer = () => {
    setReplacePlayerState({
      showReplacePlayer: false,
      showNewPlayer: false,
      customerQuery: "",
      customerSearching: false,
      playerSearchResult: [],
      selectedCustomer: null,
      currentCustomer: null,
      slot: null,
      slotFound: null,
    });
  };

  const handleReplacePlayerSelection = (_id: number, customer: ICustomer) => {
    setReplacePlayerState(prevState => ({ ...prevState, selectedCustomer: customer }));
  };

  const handleReplacePlayerSearch = (query: string) => {
    setReplacePlayerState(prevState => ({ ...prevState, customerQuery: query }));
  };

  const replaceGolfer = (
    slot: ISlot,
    slotFound: { id: number; customer_id: number; guest: boolean },
    newCustomer: ICustomer,
  ) => {
    const deleteIndex = editBookingState.teeTimeSlots.indexOf(slotFound);
    if (deleteIndex === -1) {
      return;
    }

    const duplicateOnOtherSlot = editBookingState.teeTimeSlots.some((teeTimeSlot, idx) => {
      if (teeTimeSlot.id === slotFound.id) {
        return false;
      }

      if (teeTimeSlot.guest) {
        return false;
      }

      return editBookingState.customerSelection[idx]?.id === newCustomer.id;
    });

    if (duplicateOnOtherSlot) {
      dispatch(showError(t("This customer is already on the booking.")));
      return;
    }

    let customerSelection = [...editBookingState.customerSelection];
    let teeTimeSlots = [...editBookingState.teeTimeSlots];
    let selectedPaymentMethod = editBookingState.selectedPaymentMethod;

    if (deleteIndex === 0) {
      selectedPaymentMethod = undefined;
    }

    const playerAndGuests = editBookingState.teeTimeSlots.filter(
      teeTimeSlot => teeTimeSlot.customer_id === slotFound.customer_id,
    );

    if (playerAndGuests.length > 1 && slotFound.guest === false) {
      customerSelection = customerSelection.filter(customer => customer.id !== slotFound.customer_id);
      teeTimeSlots = teeTimeSlots.filter(teeTimeSlot => teeTimeSlot.customer_id !== slotFound.customer_id);
    } else {
      customerSelection.splice(deleteIndex, 1);
      teeTimeSlots.splice(deleteIndex, 1);
    }

    customerSelection.splice(slot.position - 1, 0, newCustomer);
    teeTimeSlots.splice(slot.position - 1, 0, {
      id: slot.id,
      customer_id: newCustomer.id,
      guest: false,
    });

    updateState<IEditBookingState>(
      { customerSelection, teeTimeSlots, selectedPaymentMethod },
      StateType.EditBookingState,
    );
  };

  const confirmReplacePlayer = () => {
    if (!replacePlayerState.selectedCustomer || !replacePlayerState.slot || !replacePlayerState.slotFound) {
      return;
    }

    replaceGolfer(
      replacePlayerState.slot,
      replacePlayerState.slotFound,
      replacePlayerState.selectedCustomer,
    );
    closeReplacePlayer();
  };

  const createReplacePlayerCustomer = async (customerInfo: ICustomerInfoState) => {
    const customer = await createCustomer(customerInfo);
    if (customer) {
      setReplacePlayerState(prev => ({
        ...prev,
        showNewPlayer: false,
        selectedCustomer: customer,
      }));
    }
  };

  const replacePlayerSearch = async (
    mounted: boolean,
    golferSearchQuery: string,
    cancelToken: CancelToken,
  ) => {
    try {
      if (golferSearchQuery === "") {
        if (mounted) {
          setReplacePlayerState(prevState => ({ ...prevState, playerSearchResult: [] }));
        }
        return;
      }

      setReplacePlayerState(prevState => ({ ...prevState, customerSearching: true }));
      const results = await loadCustomers(golferSearchQuery, cancelToken);

      if (mounted) {
        setReplacePlayerState(prevState => ({
          ...prevState,
          customerSearching: false,
          playerSearchResult: results as ICustomer[],
        }));
      }
    } catch (error) {
      console.log("err", error);
    }
  };

  const onSelectCreditCard = (id: number) => {
    if (id > 0) {
      if (id === editBookingState.selectedPaymentMethod) {
        // unselect
        updateState<IEditBookingState>({ selectedPaymentMethod: undefined }, StateType.EditBookingState);
      } else {
        updateState<IEditBookingState>({ selectedPaymentMethod: id, addCard: false }, StateType.EditBookingState);
      }
    } else {
      dispatch(showError(t("Error selecting credit card.")));
    }
  };

  const loadCustomersPaymentMethods = async (customer_id: number) => {
    const paymentMethodRes = await GetCustomerPaymentMethod({ customer_id }, false);
    if (paymentMethodRes.status === StatusCode.OK) {
      //update paymentMethods and selectedPaymentMethod ONLY if original customer
      updateState<IEditBookingState>(
        {
          paymentMethods: paymentMethodRes.data,
          selectedPaymentMethod:
            selectedBooking.customer_id === customer_id ? selectedBooking.customer_payment_method_id : null,
        },
        StateType.EditBookingState,
      );
    } else {
      dispatch(showError(paymentMethodRes.message));
    }
  };

  const loadAuditTrail = async () => {
    const auditTrailRes = await GetAuditTrails({ tee_time_booking_id: selectedBooking.id }, false);
    if (auditTrailRes.status === StatusCode.OK) {
      updateState<IEditBookingState>({ auditTrails: auditTrailRes.data }, StateType.EditBookingState);
    } else {
      dispatch(showError(t("Error loading audit trail")));
    }
  };

  //Function to save card to customer and to booking
  const setupCard = async (payment_method: string, booking_id: number, customer_id: number) => {
    const setupRes = await PostSetup({ customer_id }, true);

    console.log("setupRes", setupRes);

    if (setupRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error setting up payment method")));
      return false;
    } else {
      if (payment_method !== "undefined") {
        const stripeRes = await stripe.confirmCardSetup(setupRes.data.setup_intent.client_secret as string, {
          payment_method: payment_method,
        });

        console.log("stripeRes", stripeRes);

        if (stripeRes.error) {
          dispatch(showError(stripeRes.error.message));
        } else {
          const methodRes = await PostPaymentMethod({
            payment_method_id: stripeRes.setupIntent.payment_method,
            customer_id: customer_id,
            save_card: true,
            save_on_profile: editBookingState.saveCard,
          });
          console.log("methodRes", methodRes);
          if (methodRes.status !== StatusCode.OK) {
            dispatch(showError(methodRes.data.message as string));
            return false;
          } else {
            updateState<IEditBookingState>({ bookingPosting: true }, StateType.EditBookingState);
            dispatch(showSuccess(methodRes.data.message as string));
            const bookingRes = await PutBooking(
              {
                customer_payment_method_id: methodRes.data.id,
                id: booking_id,
                quantity: Number(editBookingState.quantity),
                holes: Number(editBookingState.holes),
                power_cart_quantity: Number(editBookingState.powerCartQuantity),
                tee_time_slots: editBookingState.teeTimeSlots,
              },
              true,
            );
            if (bookingRes.status !== StatusCode.OK) {
              updateState<IEditBookingState>({ bookingPosting: false }, StateType.EditBookingState);
              dispatch(showError(bookingRes.message));
              return false;
            }
            dispatch(showSuccess(bookingRes.message));
            return true;
          }
        }
      }
    }
  };

  //Function to save card
  const stripe = useStripe();
  const elements = useElements();

  const collectCard = async (booking_id: number, customer_id: number) => {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occurred before card could save. Please reattempt.")));
      return false;
    }

    // Loader for length of operation to stop user from clicking
    dispatch(enqueue());
    try {
      let setupCardRes;
      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (paymentMethod.paymentMethod) {
        setupCardRes = await setupCard(paymentMethod.paymentMethod.id, booking_id, customer_id);
        if (setupCardRes) {
          dispatch(dequeue());
          return true;
        } else {
          dispatch(dequeue());
          return false;
        }
      } else {
        dispatch(dequeue());
        dispatch(showError(paymentMethod.error.message));
        return false;
      }
    } catch (e) {
      console.error(e);
      dispatch(dequeue());
    }

    dispatch(dequeue());
  };

  // const secondaryTabs = [
  //   {
  //     id: "booking-copy",
  //     icon: faCopy,
  //     onSelect: () => handleSecondaryTabToggle("copy"),
  //   },
  //   {
  //     id: "booking-reoccur",
  //     icon: faArrowRight,
  //     onSelect: () => handleSecondaryTabToggle("move"),
  //   },
  //   {
  //     id: "booking-delete",
  //     icon: faTrash,
  //     onSelect: () => handleSecondaryTabToggle("cancel"),
  //   },
  // ];

  /** Toggles specified modal of the secondary tab given.  If no tab is passed -- closes all secondary tabs */
  function handleSecondaryTabToggle(tab?: "copy" | "move" | "cancel") {
    switch (tab) {
      case "copy": {
        setEditBookingState({ ...editBookingState, copyBookingFutureDates: [], copyBookingIdList: [] });
        setDuplicateBookingPopoverIsVisible(!duplicateBookingPopoverIsVisible);
        if (moveBookingPopoverIsVisible) {
          setMoveBookingPopoverIsVisible(false);
        }
        if (cancelBookingPopoverIsVisible) {
          setCancelBookingPopoverIsVisible(false);
        }

        break;
      }
      case "move": {
        setMoveBookingPopoverIsVisible(!moveBookingPopoverIsVisible);
        if (cancelBookingPopoverIsVisible) {
          setCancelBookingPopoverIsVisible(false);
        }
        if (duplicateBookingPopoverIsVisible) {
          setDuplicateBookingPopoverIsVisible(false);
        }

        break;
      }
      case "cancel": {
        setCancelBookingPopoverIsVisible(!cancelBookingPopoverIsVisible);
        if (moveBookingPopoverIsVisible) {
          setMoveBookingPopoverIsVisible(false);
        }
        if (duplicateBookingPopoverIsVisible) {
          setDuplicateBookingPopoverIsVisible(false);
        }

        break;
      }
      default: {
        setEditBookingState({ ...editBookingState, copyBookingFutureDates: [], copyBookingIdList: [] });
        setDuplicateBookingPopoverIsVisible(false);
        setMoveBookingPopoverIsVisible(false);
        setCancelBookingPopoverIsVisible(false);
      }
    }
  }

  const [selected, setSelected] = useState(uiStore.editBookingStartTab);

  useEffect(() => {
    setSelected(uiStore.editBookingStartTab);
  }, [uiStore.editBookingStartTab]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);

    if (selectedTabIndex === 0) {
      updateState<IEditBookingState>(
        {
          elementComplete: {
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
          },
        },
        StateType.EditBookingState,
      );
    }

    if (selectedTabIndex === 2) {
      void getBookingNotes();
    }
  };

  const bookingSourceOptions: Array<{
    value: "phone" | "walk_in";
    label: string;
    icon: typeof faPhone;
  }> = [
    { value: "phone", label: t("Phone"), icon: faPhone },
    { value: "walk_in", label: t("Walk In"), icon: faDoorOpen },
  ];

  const sourceMenu = () => {
    return (
      <ul className="extra-players-menu booking-source-menu">
        {bookingSourceOptions.map(option => (
          <li
            key={option.value}
            className="extra-players-menu-item"
            onClick={() => selectBookingSource(option.value)}
          >
            <FontAwesomeIcon icon={option.icon} className="mr-2" />
            <span>{option.label}</span>
          </li>
        ))}
      </ul>
    );
  };

  const bookingSourceLocked = isLockedBookingSource(selectedBooking?.source);

  const selectBookingSource = (source: "phone" | "walk_in") => {
    updateState<IEditBookingState>(
      { source, sourceDropdownVisible: false },
      StateType.EditBookingState,
    );
  };

  const handleSourceDropdownVisible = () => {
    updateState<IEditBookingState>({ sourceDropdownVisible: true }, StateType.EditBookingState);
  };

  const handleHolesChange = (value: number) => {
    const holes = value;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      let updatedPowerCartQuantity: number = null;

      if (holes === 9 && editBookingState.powerCartQuantity > bookingNineHoleCartsAvailable) {
        updatedPowerCartQuantity = bookingNineHoleCartsAvailable;
      } else if (holes === 18 && editBookingState.powerCartQuantity > bookingEighteenHoleCartsAvailable) {
        updatedPowerCartQuantity = bookingEighteenHoleCartsAvailable;
      } else {
        updatedPowerCartQuantity = editBookingState.powerCartQuantity;
      }

      updateState<IEditBookingState>(
        { holes, powerCartQuantity: updatedPowerCartQuantity },
        StateType.EditBookingState,
      );
    } else {
      updateState<IEditBookingState>({ holes }, StateType.EditBookingState);
    }
  };

  const handlePlayersChange = (value: number) => {
    const quantity = value;
    // If the new selected quanitity is less than the previous quantity, remove the extra players
    if (quantity < editBookingState.quantity) {
      void handleRemoverGolfers(quantity);
    }
    updateState<IEditBookingState>({ quantity }, StateType.EditBookingState);
  };

  const handleRemoverGolfers = (newQuantity: number) => {
    const updatedCustomerSelection = editBookingState.customerSelection.filter(
      (customer, index) => index + 1 <= newQuantity,
    );

    const updatedTeeTimeSlots = editBookingState.teeTimeSlots.filter((slot, index) => index + 1 <= newQuantity);

    updateState<IEditBookingState>(
      { customerSelection: updatedCustomerSelection, teeTimeSlots: updatedTeeTimeSlots },
      StateType.EditBookingState,
    );
    updateState<IMainState>(
      { golferSearchQuery1: "", golferSearchQuery2: "", golferSearchQuery3: "", golferSearchQuery4: "", golferSearchQuery5: "", golferSearchQuery6: "" },
      StateType.MainState,
    );
  };

  const handleCartChange = (value: number) => {
    const powerCartQuantity = value;
    updateState<IEditBookingState>({ powerCartQuantity }, StateType.EditBookingState);
  };

  const handleResendConfirmation = async () => {
    const putBookingConfirmationResponse = await PutBookingConfirmation(
      { booking_group_id: selectedBooking.booking_group_id },
      true,
    );

    if (putBookingConfirmationResponse.status === StatusCode.OK) {
      dispatch(showSuccess(t("Booking Confirmation Sent")));
    } else {
      dispatch(showError(t("Failed to send booking confirmation")));
    }
  };

  const toggleNewCustomerSheet = (slot: ISlot) => {
    updateState<INewPlayerState>(
      {
        selectedSlot: slot,
        newCustomerSheetActive: !newPlayerState.newCustomerSheetActive,
      },
      StateType.NewPlayerState,
    );

    openNewCustomerSheet();
  };

  useEffect(() => {
    if (!editBookingState.customerSelection[0]?.id) {
      updateState<IEditBookingState>(
        { selectedPaymentMethod: undefined, paymentMethods: undefined },
        StateType.EditBookingState,
      );
      updateState<IMainState>({ playingPartners: [] }, StateType.MainState);
      return;
    }

    //Load paymentMethods if the first player changes
    if (editBookingState.originalCustomerSelection[0] !== editBookingState.customerSelection[0]) {
      void loadCustomersPaymentMethods(editBookingState.customerSelection[0].id);
    }

    //Load playing partners of first player to pre-populate empty golfer dropdowns
    void loadPlayingPartners(editBookingState.customerSelection[0].id);
  }, [editBookingState.customerSelection[0]]);

  useEffect(() => {
    if (!replacePlayerState.showReplacePlayer) {
      return;
    }

    const source = axios.CancelToken.source();
    let mounted = true;
    const timeoutId = window.setTimeout(() => {
      void replacePlayerSearch(mounted, replacePlayerState.customerQuery, source.token);
    }, 500);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setReplacePlayerState(prevState => ({ ...prevState, playerSearchResult: [] }));
      source.cancel("Cancelled");
    };
  }, [replacePlayerState.customerQuery, replacePlayerState.showReplacePlayer]);

  const loadPlayingPartners = async (customer_id: number) => {
    const partnersRes = await GetCustomerFriends({ customer_id }, false);
    if (partnersRes.status !== StatusCode.OK) {
      updateState<IMainState>({ playingPartners: [] }, StateType.MainState);
      return;
    }

    //Normalize ICustomerFriend to match ICustomer shape used in dropdown render
    const normalized: Partial<ICustomer>[] = (partnersRes.data ?? []).map((friend: ICustomerFriend) => ({
      id: friend.id,
      full_name: friend.full_name,
      first_name: friend.first_name,
      last_name: friend.last_name,
      email: friend.email,
      phone: friend.phone,
      member_code: friend.member_code,
      member_number: friend.member_number ?? null,
      customer_type: Array.isArray(friend.customer_type)
        ? friend.customer_type.map(type => type.title).join(", ")
        : "",
    }));

    updateState<IMainState>({ playingPartners: normalized }, StateType.MainState);
  };

  const elementsComplete =
    editBookingState.elementComplete.cardNumber === true &&
    editBookingState.elementComplete.cardExpiry === true &&
    editBookingState.elementComplete.cardCvc === true;

  const cc_required = teeSheetStore.selectedTeeTime?.credit_card_required;
  const cc_unselected = editBookingState.selectedPaymentMethod === undefined && !elementsComplete;
  const cc_override = permissions?.tee_sheet_credit_card_override;

  const handleCardSectionChange = (
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) => {
    if (editBookingState.selectedPaymentMethod !== undefined) {
      updateState<IEditBookingState>({ selectedPaymentMethod: undefined }, StateType.EditBookingState);
    }

    updateState<IEditBookingState>(
      {
        elementComplete: { ...editBookingState.elementComplete, [e.elementType]: e.complete },
      },
      StateType.EditBookingState,
    );
  };

  function openNewCustomerSheet() {
    setNewPlayerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: true,
    }));
  }

  useEffect(() => {
    const selectedTeeTimeDateTime = new Date(
      moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LLL"),
    );

    const hourBeforeSelectedTeeTimeDateTime = new Date(
      moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LLL"),
    );

    hourBeforeSelectedTeeTimeDateTime.setHours(hourBeforeSelectedTeeTimeDateTime.getHours() - 1);

    const currentTime = moment();

    const bookingWithinHour = currentTime.isBetween(hourBeforeSelectedTeeTimeDateTime, selectedTeeTimeDateTime);

    updateState<IEditBookingState>({ bookingWithinHour: bookingWithinHour }, StateType.EditBookingState);
  }, [uiStore.editBookingActive]);


  async function removeBookingNote(id: number) {
    const res = await DeleteBookingNote({ booking_note_id: id }, true);

    if (Number(res.status) !== Number(StatusCode.OK)) {
      return;
    }
    console.log(res);

    // Generate AUDIT LOG ?

    // REFRESH NOTES
    updateState<IEditBookingState>(
      { notesTitleVisible: "block", notes: editBookingState.notes.filter((note: any) => note.id !== id) },
      StateType.EditBookingState,
    );
  }

  // async function editBookingNote() {
  //   const noteInfo = {
  //     booking_note_id: editNoteModal.id,
  //     content: editNoteModal.content,
  //   };

  //   const res = await PutBookingNote(noteInfo, true);
  //   console.log(res);

  //   if (res.status !== StatusCode.OK) {
  //     return;
  //   }

  //   const { ...wantedParams } = res.data;

  //   updateState<IEditBookingState>(
  //     {
  //       notesTitleVisible: "block",
  //       notes: editBookingState.notes.map((note: any) => {
  //         if (note.id === wantedParams.id) {
  //           return wantedParams as Record<string, any>;
  //         } else {
  //           return note as Record<string, any>;
  //         }
  //       }),
  //     },
  //     StateType.EditBookingState,
  //   );

  //   closeEditNoteModal();
  // }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;

    updateState<IEditBookingState>(
      {
        [id]: checked,
      },
      StateType.EditBookingState,
    );
  }

  const handleAddNewCard = async () => {
    if (!editBookingState.teeTimeSlots[0]?.customer_id) {
      return;
    }

    const success = await collectCard(
      selectedBooking.id,
      editBookingState.teeTimeSlots[0].customer_id,
    );

    if (success) {
      closeAddCardModal();
    }
  };


  // Controls inline note composer card
  const [showInlineNewNote, setShowInlineNewNote] = useState(false);
  const [inlineNoteContent, setInlineNoteContent] = useState("");
  const [inlineNoteParticipant, setInlineNoteParticipant] = useState<number>(null);

  return (
    <div>
      <NewCustomer
        newCustomerSheetActive={newPlayerState.newCustomerSheetActive}
        onCancel={closeNewCustomer}
        onOk={info => void addCustomer(info)}
        searchValue={newPlayerState.currentSearch}
      />

      <Portal isMounted={uiStore.editBookingActive}>
        <Sheet
          open={uiStore.editBookingActive}
          size="medium"
          closable={!editBookingState.bookingPosting}
          title={
            <>
              {t("Edit Booking")}
              <span className="booking-confirmation-number">
              </span>
            </>
          }
          supportText={
            `${t("View, manage and edit the details of")} #${selectedBooking?.confirmation_number}`
          }

          height="flexible"
          overflow
          onCancel={closeBooking}
          onOk={
            !cc_override && cc_required && cc_unselected && !editBookingState.bookingWithinHour
              ? () => handleTabChange(1)
              : handleUpdateBooking
          }
          cancelText={t("Cancel")}
          okText={
            !cc_override && cc_required && cc_unselected && !editBookingState.bookingWithinHour
              ? t("Enter Credit Card")
              : t("Save Booking")
          }
          okDisabled={
            editBookingState.customerSelection.length === 0 ||
            editBookingState.holes === null ||
            editBookingState.quantity === null ||
            editBookingState.powerCartQuantity === null ||
            editBookingState.bookingPosting
          }
          cancelDisabled={editBookingState.bookingPosting}
          backDropCancel={false}
        >
          {props.loading || editBookingState.bookingPosting ? (
            <div className="text-center">
              <div style={{ height: "32px", overflow: "hidden" }}>
                <Spin />
              </div>
              <div className="mt-2">{props.loading ? props.loadingText : "Saving Booking"}</div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <div className="booking-datetime">
                  <div className="booking-datetime-time">
                    {moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LT")}
                  </div>

                  <div className="booking-datetime-date">
                    {moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format("LL")}
                  </div>
                </div>

                <NavigationDropdownNew
                  label={<Button size={"medium"} type="secondary"><FontAwesomeIcon icon={faEllipsisV} /></Button>}
                  showPlainTextLabel
                  rightAlign
                  sections={[[
                    {
                      icon: "arrow-right-arrow-left",
                      label: t("Move booking"),
                      onClick: () => handleSecondaryTabToggle("move"),
                    },
                    {
                      icon: "layer-group",
                      label: t("Duplicate booking"),
                      onClick: () => {
                        void openDuplicateBookingModal(selectedBooking, teeSheetStore?.selectedTeeTime);
                        void closeBooking();
                      },
                    },
                    {
                      icon: "trash-can",
                      label: t("Delete"),
                      onClick: () => handleSecondaryTabToggle("cancel"),
                    },
                  ]]}
                />



              </div>

              {moveBookingPopoverIsVisible && (
                <MoveBookingTab
                  isActive={true}
                  bookingStateQuantity={editBookingState.quantity}
                  bookingStateHoles={editBookingState.holes}
                  course_id={teeSheetStore.selectedTeeTime?.course_id}
                  confirmMoveBooking={(id, resend) => void confirmMoveBooking(id, resend)}
                  userLevel="facility"
                  closePopover={() => setMoveBookingPopoverIsVisible(false)}
                />
              )}


              {duplicateBookingPopoverIsVisible && (
                <DuplicateBookingTab
                  isActive={true}
                  copyBookingFutureDates={editBookingState.copyBookingFutureDates}
                  bookingStateHoles={editBookingState.holes}
                  bookingStateQuantity={editBookingState.quantity}
                  addCopyBookingIdToList={addCopyBookingIdToList}
                  confirmCopyBooking={id => void confirmCopyBooking(id)}
                  userLevel="facility"
                />
              )}

              <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} variant="underline">


                <div>
                  <Popup
                    open={cancelBookingPopoverIsVisible}
                    type="delete"
                    title={t("Delete booking")}
                    description={t("Are you sure you want to delete this booking? This action cannot be undone.")}
                    onCancel={() => setCancelBookingPopoverIsVisible(false)}
                    onOk={confirmCancelBooking}
                    cancelText={t("Cancel")}
                    okText={t("Delete")}
                    aligned="left"
                    onOkType="error"
                  >
                    <div className="bm-cancel-booking-toggle-wrapper mt-4">
                      <Toggle
                        id="send_cancellation_email"
                        size="small"
                        checked={editBookingState.send_cancellation_email}
                        onChange={handleCheckboxChange}
                        labelRight={"Send cancellation email"}
                      />
                    </div>
                  </Popup>
                </div>

                {selected === 0 ? (
                  <>
                    <div className="booking-modal">
                      <div className="booking-details">
                        <div className="booking-options">
                          <div className="booking-options_option carts-section">
                            <h2 className="carts-section-title">{t("Holes")}</h2>
                            <Radio.Group onChange={handleHolesChange} value={editBookingState.holes}>
                              {editBookingState.holeButtons?.map((disabled, i) => {
                                const holes = i === 0 ? 18 : 9;
                                return (
                                  <Radio.Button key={`holes_edit_${holes}`} value={holes} disabled={disabled}>
                                    {convertHoles(holes, facilityStore.facility?.courses?.find(course => course.id === teeSheetStore.selectedTeeTime?.course_id))}
                                  </Radio.Button>
                                );
                              })}
                            </Radio.Group>
                          </div>

                          <div className="booking-options_option carts-section">
                            <h2 className="carts-section-title">{t("Players")}</h2>
                            <Radio.Group
                              name="players_edit"
                              onChange={handlePlayersChange}
                              value={editBookingState.quantity}
                            >
                              {editBookingState.playerButtons?.map((disabled, i) => {
                                const players = i + 1;
                                return (
                                  <Radio.Button key={`players_edit_${players}`} value={players} disabled={disabled}>
                                    {players}
                                  </Radio.Button>
                                );
                              })}
                            </Radio.Group>
                          </div>
                          <div className="booking-options_option carts-section">
                            <h2 className="carts-section-title">{t("Carts")}</h2>
                            <Radio.Group
                              name="power_carts_edit"
                              onChange={handleCartChange}
                              value={editBookingState.powerCartQuantity}
                            >
                              <Radio.Button value={0} disabled={teeSheetStore.selectedTeeTime?.cart_rule == "required"}>
                                0
                              </Radio.Button>
                              <Radio.Button
                                value={1}
                                disabled={
                                  teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                  (bookingCartsAvailable < 1 &&
                                    facilityStore.teeSheetSettings?.validate_power_cart_count)
                                }
                              >
                                1
                              </Radio.Button>
                              <Radio.Button
                                value={2}
                                disabled={
                                  teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                  (bookingCartsAvailable < 2 &&
                                    facilityStore.teeSheetSettings?.validate_power_cart_count)
                                }
                              >
                                2
                              </Radio.Button>
                            </Radio.Group>
                          </div>
                          {facilityStore.teeSheetSettings?.display_booking_source && !bookingSourceLocked ? (
                            <div className="booking-options_option carts-section booking-source-option">
                              <h2 className="carts-section-title">{t("Source")}</h2>
                              <div className="flex flex-row">
                                <Radio.Group name="source_edit" value={editBookingState.source}>
                                  <Radio.Button
                                    onClick={handleSourceDropdownVisible}
                                    value={editBookingState.source}
                                  >
                                    {editBookingState.source === "phone" || editBookingState.source === "walk_in" ? (
                                      <FontAwesomeIcon
                                        icon={
                                          bookingSourceOptions.find(option => option.value === editBookingState.source)
                                            ?.icon ?? faPhone
                                        }
                                        title={
                                          bookingSourceOptions.find(option => option.value === editBookingState.source)
                                            ?.label ?? t("Phone")
                                        }
                                      />
                                    ) : editBookingState.source === "tee_sheet" ? (
                                      <FontAwesomeIcon icon={faDesktop} title={t("Tee Sheet")} />
                                    ) : (
                                      <span
                                        title={
                                          selectedBooking?.source_title ||
                                          editBookingState.source
                                            .replace(/_/g, " ")
                                            .replace(/(^\w|\s\w)/g, c => c.toUpperCase())
                                        }
                                      >
                                        <BookingSourceIcon source={editBookingState.source} className="" />
                                      </span>
                                    )}
                                  </Radio.Button>
                                </Radio.Group>
                                <Dropdown
                                  overlay={sourceMenu()}
                                  trigger="boolean"
                                  placement="topLeft"
                                  className="self-center booking-source-menu"
                                  showMenu={editBookingState.sourceDropdownVisible}
                                  propRef={sourceRef}
                                  outSideClickDisable={true}
                                >
                                  <span></span>
                                </Dropdown>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <div className="booking-customers-list" style={{ marginTop: "12px" }}>
                          {teeSheetStore.selectedTeeTime?.slots
                            ?.filter(
                              filteredSlot =>
                                filteredSlot.status === "available" ||
                                filteredSlot?.booking?.id === selectedBooking?.id,
                            )
                            .map((slot: ISlot, index: number) => {
                              const golferPosition = slot.position.toString();
                              const queryKey = "golferSearchQuery".concat(golferPosition);
                              const searchResultKey = "golferSearchResult".concat(golferPosition);
                              //Get the matching slot that was added to the selection
                              const slotFound = editBookingState.teeTimeSlots.find(
                                (element: any) => element.id === slot.id,
                              );

                              if (slotFound) {
                                //Get the golfer info from current slot
                                const golfer = editBookingState.customerSelection.find(
                                  golfer => golfer?.id === slotFound.customer_id,
                                );
                                if (golfer != null) {
                                  return (
                                    <PlayerSlots
                                      closable
                                      removeGolfer={() => removeGolfer(slotFound)}
                                      onPlayerClick={() => openReplacePlayer(slot, slotFound, golfer)}
                                      email={golfer?.email}
                                      name={golfer?.full_name}
                                      memberCode={golfer?.member_code}
                                      customerType={golfer?.customer_type}
                                      phone={golfer?.phone}
                                      key={index}
                                      isGuest={slotFound.guest}
                                    />
                                  );
                                }
                              } else if (
                                slot.booking?.id === selectedBooking?.id ||
                                (slot.status === "available" && !teeTimeLock)
                              ) {
                                //Display booking if slot is available or if slot is apart of this booking
                                return (
                                  <div key={index} className="booking-player-dropdown">
                                    <Select
                                      key={index}
                                      label={t("Player")}
                                      placeholder={t("Golfer")}
                                      showSearch
                                      className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                                      onSearch={(query: string) => handleCustomerSearch(query, queryKey)}
                                      onChange={(id: string | number, slot: ISlot) =>
                                        handleCustomerSelection(id, slot, null)
                                      }
                                      allowClear
                                      searchValue={state[queryKey]}
                                      showDropDownOnFocus={true}
                                      searching={state.searching}
                                      disabled={
                                        editBookingState?.customerSelection?.length >= editBookingState?.quantity ||
                                          index + 1 > editBookingState.quantity
                                          ? true
                                          : false
                                      }
                                      usePortal
                                      dropDownPositionBottom
                                      autoFocus={slot.position === editBookingState.customerSelection?.length + 1}
                                    >
                                      <Select.Action
                                        className="ui-select-dropdown-list-item"
                                        onClick={() => toggleNewCustomerSheet(slot)}
                                      >
                                        <p className="text-md">{t("New Customer")}</p>
                                      </Select.Action>

                                      {(((): (Partial<ICustomer> | ICustomer)[] => {
                                        const selectedIds = editBookingState.customerSelection.map(customer => customer?.id);
                                        const partnerOptions = state.playingPartners.filter(
                                          partner => !selectedIds.includes(partner.id),
                                        );
                                        return state[queryKey]
                                          ? (state[searchResultKey] as (Partial<ICustomer> | ICustomer)[])
                                          : partnerOptions;
                                      })()).map((golfer: any, i: number) => {
                                        const playingPartnerIds = new Set(
                                          state.playingPartners.map(partner => Number(partner.id)),
                                        );
                                        const isPlayingPartner = playingPartnerIds.has(Number(golfer?.id));
                                        const restrictions = teeSheetStore.selectedTeeTime.active_restrictions?.filter(restriction => restriction.customer_type_id === golfer?.customer_type_id);
                                        //Banned golfer
                                        if (golfer != null && (golfer?.banned || (restrictions?.filter(restriction => restriction.block_bookings).length > 0))) {
                                          return (
                                            <div
                                              key={i}
                                              className="flex justify-between bg-gray-100 cursor-not-allowed"
                                              style={{ padding: "8px 16px" }}
                                            >
                                              <div>
                                                <div className="font-semibold text-lg flex items-center gap-2">
                                                  <span>{golfer?.full_name}</span>
                                                  {isPlayingPartner ? (
                                                    <Badge size="small" type="gray">
                                                      {t("Playing Partner")}
                                                    </Badge>
                                                  ) : null}
                                                </div>
                                                <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                <div className="text-sm text-gray-500">{golfer.email}</div>
                                                <div className="text-sm text-gray-500">
                                                  {golfer.phone ? golfer.phone : null}
                                                </div>
                                              </div>
                                              <div className="flex flex-column justify-between">
                                                <div className="flex flex-column gap-2">
                                                  {golfer.has_high_priority_notes ? (
                                                    <div
                                                      className="font-medium text-error self-end gap-2 flex"
                                                      style={{ alignItems: "center" }}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                      <p>{t("Click to view notes")}</p>
                                                    </div>
                                                  ) : (
                                                    <div />
                                                  )}
                                                  {golfer?.banned ? (
                                                    <div className="ml-auto">
                                                      <Badge size="small" type="warning">
                                                        {t('Banned')}
                                                      </Badge>
                                                    </div>
                                                  ) : null}
                                                  {restrictions?.length > 0 && !golfer?.banned ? (
                                                    <OverlayTrigger
                                                      placement="top-end"
                                                      overlay={
                                                        <Tooltip id="restricted-booking-tooltip">{`${t(
                                                          "Tee time outside of customer booking hours",
                                                        )}`}</Tooltip>
                                                      }
                                                    >
                                                      <div className="ml-auto">
                                                        <Badge size="small" type="warning">
                                                          <FontAwesomeIcon className="mr-1" icon={faCircleExclamation} />
                                                          {t('Restricted')}
                                                        </Badge>
                                                      </div>
                                                    </OverlayTrigger>
                                                  ) : null}
                                                </div>
                                                <div className="font-medium text-base text-gray-500 self-end">
                                                  {golfer.member_code}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }
                                        if (golfer !== null && golfer !== undefined) {
                                          return (
                                            <Option key={i} value={golfer.id} extraValues={slot}>
                                              <div className="flex justify-between">
                                                <div>
                                                  <div className="font-semibold text-lg flex items-center gap-2">
                                                    <span>{golfer?.full_name}</span>
                                                    {isPlayingPartner ? (
                                                      <Badge size="small" type="gray">
                                                        {t("Playing Partner")}
                                                      </Badge>
                                                    ) : null}
                                                  </div>
                                                  <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                  <div className="text-sm text-gray-500">{golfer.email}</div>
                                                  <div className="text-sm text-gray-500">
                                                    {golfer.phone ? golfer.phone : null}
                                                  </div>
                                                </div>
                                                <div className="flex flex-column justify-between">
                                                  {golfer.has_high_priority_notes ? (
                                                    <div
                                                      className="font-medium text-error self-end gap-2 flex"
                                                      style={{ alignItems: "center" }}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                      }}
                                                    >
                                                      <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                      <p>{t("Click to view notes")}</p>
                                                    </div>
                                                  ) : (
                                                    <div />
                                                  )}
                                                  {restrictions?.length > 0 ? (
                                                    <OverlayTrigger
                                                      placement="top-end"
                                                      overlay={
                                                        <Tooltip id="restricted-booking-tooltip">{`${t(
                                                          "Tee time outside of customer booking hours",
                                                        )}`}</Tooltip>
                                                      }
                                                    >
                                                      <div className="ml-auto">
                                                        <Badge size="small" type="warning">
                                                          <FontAwesomeIcon className="mr-1" icon={faCircleExclamation} />
                                                          {t('Restricted')}
                                                        </Badge>
                                                      </div>
                                                    </OverlayTrigger>
                                                  ) : null}
                                                  <div className="font-medium text-base text-gray-500 self-end">
                                                    {golfer.member_code}
                                                  </div>
                                                </div>
                                              </div>
                                            </Option>
                                          );
                                        }
                                      })}
                                    </Select>
                                  </div>
                                );
                              }
                            })}
                        </div>
                      </div>
                      <div className="flex items-end justify-end gap-4">
                        <Button type="link" onClick={() => void handleResendConfirmation()}>
                          <FontAwesomeIcon
                            icon={faRotateRight}
                            style={{ marginRight: 8, fontSize: 16 }}
                          />
                          {t("Resend Confirmation")}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : null}
                {selected === 1 ? (
                  <div className="booking-credit-card-tab">
                    <CreditCardCatalogue
                      customerId={editBookingState.teeTimeSlots[0]?.customer_id}
                      creditCards={editBookingState.paymentMethods}
                      selectedCard={editBookingState.selectedPaymentMethod}
                      setSelectedCard={onSelectCreditCard}
                    />

                    <CreditCardAdd
                      isSavingCard={editBookingState.saveCard}
                      setIsSavingCard={() =>
                        updateState<IEditBookingState>(
                          { saveCard: !editBookingState.saveCard },
                          StateType.EditBookingState,
                        )
                      }
                      handleStripeInputs={handleCardSectionChange}
                      onOpenModal={() => updateAddCardModal({ isOpen: true })}
                    />
                  </div>

                ) : null}

                {selected === 2 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingBottom: '12px',
                      //borderBottom: '1px solid #E5E7EB'
                    }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginTop: '12px',
                        }}
                      >
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                          {t("Notes")}
                        </span>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '20px',
                          height: '20px',
                          padding: '0 6px',
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: '500',
                          lineHeight: '1',
                        }}>
                          {editBookingState.notes?.length || 0}
                        </span>
                      </div>
                      <ButtonNew
                        type="text"
                        size="small"
                        onClick={() => {
                          setInlineNoteContent("");
                          setShowInlineNewNote(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '4px' }} />
                        {t('Add note')}
                      </ButtonNew>
                    </div>

                    {showInlineNewNote && (
                      <Card
                      >
                        <Card.Section>
                          <TextField
                            label={t("New Note")}
                            value={inlineNoteContent}
                            onChange={e => setInlineNoteContent(e.target.value)}
                            placeholder={"Enter your note here..."}
                          />
                          <div className="pt-2 w-50">
                            <SelectNew
                              id="inlineNoteParticipant"
                              label={t("Participant")}
                              value={inlineNoteParticipant}
                              onChange={e => setInlineNoteParticipant(e)}
                              placeholder={"Select a participant..."}
                              options={teeSheetStore.selectedTeeTime?.slots
                                .filter(slot => slot.tee_time_booking_id === selectedBooking?.id).map(slot => ({
                                  label: `${slot.booking_participant.customer.full_name} ${slot.booking_participant?.guest ? " - Guest" : ""}`,
                                  id: slot.booking_participant.id,
                                }))}
                            />
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '8px',
                            marginTop: '16px',
                          }}>
                            <Button
                              type="secondary"
                              size="medium"
                              onClick={() => {
                                setShowInlineNewNote(false);
                                setInlineNoteContent("");
                              }}
                            >
                              {t("Cancel")}
                            </Button>
                            <Button
                              type="primary"
                              size="medium"
                              onClick={async () => {
                                if (!inlineNoteContent.trim()) {
                                  return;
                                }

                                const res = await PostBookingNote(
                                  {
                                    tee_time_booking_id: selectedBooking.id,
                                    content: inlineNoteContent,
                                    tee_time_booking_participant_id: inlineNoteParticipant,
                                  },
                                  true,
                                );

                                if (Number(res.status) === Number(StatusCode.OK)) {
                                  updateState<IEditBookingState>(
                                    {
                                      notes: [res.data, ...editBookingState.notes],
                                    },
                                    StateType.EditBookingState,
                                  );
                                  setShowInlineNewNote(false);
                                  setInlineNoteContent("");
                                }
                              }}
                            >
                              {t("Save note")}
                            </Button>
                          </div>
                        </Card.Section>
                      </Card>
                    )}

                    {/* Notes list or empty state */}
                    <div style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
                      {editBookingState.notes?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {editBookingState.notes.map((props: any, index: number) => (
                            <div
                              key={index}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '16px',
                              }}
                            >
                              {/* Note content */}
                              <p style={{
                                color: '#111827',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                marginBottom: '12px',
                                whiteSpace: 'pre-wrap',
                              }}>
                                {props.content}
                              </p>

                              {/* Footer with date/author and action buttons */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}>
                                {/* Date and author */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  color: '#6B7280',
                                  fontSize: '13px',
                                }}>
                                  <span>{moment(props.created_at as string).format("LLL")}</span>
                                  <span>•</span>
                                  <span>{t("Created by")} {props.author?.full_name ?? authStore.user.full_name}</span>
                                </div>

                                {/* Action buttons */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  {/* {(authStore.user.id === props.admin_user_id ||
                                    authStore.user.permissions?.bookings_edit_note) && (
                                    <button
                                      onClick={() => updateEditNoteModal({ 
                                        isOpen: true, 
                                        content: props.content, 
                                        id: props.id 
                                      })}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'white',
                                        border: '1px solid #D1D5DB',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                                        e.currentTarget.style.borderColor = '#9CA3AF';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                        e.currentTarget.style.borderColor = '#D1D5DB';
                                      }}
                                    >
                                      <FontAwesomeIcon 
                                        icon={faEdit} 
                                        style={{ color: '#6B7280', fontSize: '16px' }} 
                                      />
                                    </button>
                                  )} */}

                                  <button
                                    onClick={() => void removeBookingNote(props.id as number)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '36px',
                                      height: '36px',
                                      backgroundColor: 'white',
                                      border: '1px solid #FCA5A5',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor = '#FEF2F2';
                                      e.currentTarget.style.borderColor = '#EF4444';
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.backgroundColor = 'white';
                                      e.currentTarget.style.borderColor = '#FCA5A5';
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      style={{ color: '#EF4444', fontSize: '16px' }}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '200px',
                          padding: '24px',
                        }}>
                          <p style={{ color: '#9CA3AF', fontSize: '14px', textAlign: 'center' }}>
                            {t("No notes yet")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {selected === 3 ? (
                  <>
                    {editBookingState.auditTrails.map((audit: any, index: number) => {
                      return (
                        <div key={index} className="mt-4">
                          <AuditTrailNote
                            title={audit.title}
                            author={audit.author}
                            actions={audit.actions}
                            action={audit.action}
                            timestamp={audit.created_at}
                            last={index + 1 === editBookingState.auditTrails.length ? true : false}
                          />
                        </div>
                      );
                    })}
                  </>
                ) : null}
              </Tabs>
            </>
          )}
        </Sheet>
      </Portal>

      {/* <Portal isMounted={newBookingNoteModal.isOpen}>
        <Sheet
          title={t("New Note")}
          open={newBookingNoteModal.isOpen}
          size="small"
          onCancel={() => closeNewBookingNoteModal()}
          onOk={() => addBookingNote()}
          backDropCancel={true}
          stacked
          okDisabled={newBookingNoteModal.content.length === 0}
        >
          <TextField
            label={t("Note")}
            value={newBookingNoteModal.content}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNewBookingNote({ content: e.target.value })}
          />
        </Sheet>
      </Portal> */}

      {/* <Portal isMounted={editNoteModal.isOpen}>
        <Sheet
          title={t("Edit Note")}
          open={editNoteModal.isOpen}
          size="small"
          onCancel={() => closeEditNoteModal()}
          onOk={() => editBookingNote()}
          backDropCancel={true}
          stacked
          okDisabled={editNoteModal.content.length === 0}
        >
          <TextField
            label={t("Note")}
            value={editNoteModal.content}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateEditNoteModal({ content: e.target.value })}
          />
        </Sheet>
      </Portal> */}

      <Portal isMounted={addCardModal.isOpen}>
        <Sheet
          open={addCardModal.isOpen}
          title={t("Add new payment method")}
          size="small"
          onCancel={closeAddCardModal}
          onOk={handleAddNewCard}
          okText={t("Save")}
          cancelText={t("Cancel")}
          stacked
        >
          <div className="add-new-card-modal">

            <div className="card-fields">
              <CardSection type="two-row" onChange={handleCardSectionChange} />
              <br />
              <Checkbox
                label={t("Save card to customer profile")}
                size="small"
                checked={editBookingState.saveCard}
                onChange={() =>
                  updateState<IEditBookingState>(
                    { saveCard: !editBookingState.saveCard },
                    StateType.EditBookingState,
                  )
                }
              />
            </div>
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={replacePlayerState.showReplacePlayer}>
        <ChangeCustomer
          title={t("secure.facility.tee_sheet.tee_sheet_booking_details.230")}
          currentCustomerTitle={t("secure.facility.tee_sheet.tee_sheet_booking_details.231")}
          newCustomerTitle={t("secure.facility.tee_sheet.tee_sheet_booking_details.232")}
          okText={t("secure.facility.tee_sheet.tee_sheet_booking_details.077")}
          open={replacePlayerState.showReplacePlayer}
          changeCustomerState={replacePlayerState}
          searching={replacePlayerState.customerSearching}
          searchResults={replacePlayerState.playerSearchResult}
          newCustomerActive={replacePlayerState.showNewPlayer}
          onCancel={closeReplacePlayer}
          onOk={confirmReplacePlayer}
          handleSearchQueryChange={handleReplacePlayerSearch}
          handleCustomerSelection={handleReplacePlayerSelection}
          handleRemoveSelectedCustomer={() =>
            setReplacePlayerState(prevState => ({
              ...prevState,
              selectedCustomer: null,
              playerSearchResult: [],
              customerQuery: "",
            }))
          }
          handleCreateNewCustomer={info => void createReplacePlayerCustomer(info as ICustomerInfoState)}
          handleCloseCreateNewCustomer={() =>
            setReplacePlayerState(prevState => ({ ...prevState, showNewPlayer: false }))
          }
          handleNewCustomerActive={() =>
            setReplacePlayerState(prevState => ({ ...prevState, showNewPlayer: true }))
          }
        />
      </Portal>
    </div>
  );
};

export default BookingModalEdit;
